/**
 * Helper to determine if we should use the optimized Sector/IDX transaction fetch
 * (fetching a single _ALL.csv file instead of aggregating stocks).
 * 
 * Logic:
 * - Must be Pivot: Stock (Output: Broker)
 * - Must have Sectors selected (or IDX)
 * - Must NOT have specific tickers selected (mix of sector + ticker usually implies filtering)
 */
export const isSectorTransaction = (
    pivot: 'Broker' | 'Stock',
    _selectedTickers: string[],
    selectedSectors: string[]
): boolean => {
    // Only for Pivot Stock (Output: Broker)
    if (pivot !== 'Stock') return false;

    // Must have sectors selected
    if (selectedSectors.length === 0) return false;

    // If specific tickers are also selected (e.g. BBCA + Basic Material), 
    // standard logic usually filters stocks. 
    // If we want to support "Add Sector" to list, we might need mixed logic.
    // For now, assume if Sector is selected without specific tickers, we use Sector View.
    // actually in BrokerTransaction.tsx, selectedTickers usually empties when Sector is selected 
    // or they are mutually exclusive in UI usage often.
    // Let's assume if ANY sector is selected, we want to try sector file first for that sector item.

    return true;
};

/**
 * Get the code to use for fetching Sector/IDX transaction data.
 * Backend expects: "Basic Material_ALL", "IDX" (mapped to IDX.csv or ALL.csv logic in backend)
 */
export const getSectorTransactionCode = (sector: string): string => {
    const upper = sector.toUpperCase();
    if (upper === 'IDX' || upper === 'ALL') {
        // Backend getAzurePath handles 'IDX' or 'ALL' by looking for IDX.csv/ALL.csv
        // But our new generator makes 'IDX.csv'. 
        // Let's use 'IDX' and ensure backend maps it right, or 'ALL'.
        // In BrokerTransaction.tsx stock pivot logic, 'ALL' fetches ALL.csv.
        // Let's use 'IDX' to be explicit if we want IDX composite.
        // If backend expects 'ALL.csv' for IDX data, we might need to send 'ALL'.
        // Based on plan: "Output: Write to .../IDX.csv".
        // Backend getAzurePath: if code === 'ALL' -> ALL.csv.
        // If code === 'IDX' -> IDX.csv.
        return 'IDX';
    }

    // For sectors: "Basic Material" -> "Basic Material_ALL"
    return `${sector}_ALL`;
};

/**
 * Format Sector name for display (remove _ALL suffix if present)
 */
export const formatSectorDisplayName = (code: string): string => {
    if (code.endsWith('_ALL')) {
        return code.replace('_ALL', '');
    }
    return code;
};
